import { __assign } from "tslib";
import { invariant } from 'ts-invariant';
import { createFragmentMap, getFragmentFromSelection, } from '../../utilities/graphql/fragments';
import { getDefaultValues, getFragmentDefinitions, getOperationDefinition, } from '../../utilities/graphql/getFromAST';
import { getTypenameFromResult, makeReference, isField, resultKeyNameFromField, } from '../../utilities/graphql/storeUtils';
import { DeepMerger } from '../../utilities/common/mergeDeep';
import { shouldInclude } from '../../utilities/graphql/directives';
import { cloneDeep } from '../../utilities/common/cloneDeep';
import { maybeDeepFreeze } from '../../utilities/common/maybeDeepFreeze';
import { defaultNormalizedCacheFactory } from './entityStore';
;
var StoreWriter = (function () {
    function StoreWriter(config) {
        this.policies = config.policies;
    }
    StoreWriter.prototype.writeQueryToStore = function (_a) {
        var query = _a.query, result = _a.result, _b = _a.dataId, dataId = _b === void 0 ? 'ROOT_QUERY' : _b, _c = _a.store, store = _c === void 0 ? defaultNormalizedCacheFactory() : _c, variables = _a.variables;
        var operationDefinition = getOperationDefinition(query);
        store.retain(dataId);
        var simpleMerger = new DeepMerger;
        return this.writeSelectionSetToStore({
            result: result || Object.create(null),
            dataId: dataId,
            selectionSet: operationDefinition.selectionSet,
            context: {
                store: store,
                written: Object.create(null),
                merge: function (existing, incoming) {
                    return simpleMerger.merge(existing, incoming);
                },
                variables: __assign(__assign({}, getDefaultValues(operationDefinition)), variables),
                fragmentMap: createFragmentMap(getFragmentDefinitions(query)),
            },
        });
    };
    StoreWriter.prototype.writeSelectionSetToStore = function (_a) {
        var dataId = _a.dataId, result = _a.result, selectionSet = _a.selectionSet, context = _a.context;
        var store = context.store, written = context.written;
        var sets = written[dataId] || (written[dataId] = []);
        if (sets.indexOf(selectionSet) >= 0)
            return store;
        sets.push(selectionSet);
        var typename = getTypenameFromResult(result, selectionSet, context.fragmentMap) ||
            store.getFieldValue(dataId, "__typename") ||
            this.policies.rootTypenamesById[dataId];
        var processed = this.processSelectionSet({
            result: result,
            selectionSet: selectionSet,
            context: context,
            typename: typename,
        });
        if (processed.mergeOverrides) {
            walkWithMergeOverrides(store.get(dataId), processed.result, processed.mergeOverrides);
        }
        store.merge(dataId, processed.result);
        return store;
    };
    StoreWriter.prototype.processSelectionSet = function (_a) {
        var _this = this;
        var result = _a.result, selectionSet = _a.selectionSet, context = _a.context, mergeOverrides = _a.mergeOverrides, typename = _a.typename;
        var mergedFields = Object.create(null);
        if (typeof typename === "string") {
            mergedFields.__typename = typename;
        }
        selectionSet.selections.forEach(function (selection) {
            var _a;
            if (!shouldInclude(selection, context.variables)) {
                return;
            }
            if (isField(selection)) {
                var resultFieldKey = resultKeyNameFromField(selection);
                var value = result[resultFieldKey];
                if (typeof value !== 'undefined') {
                    var storeFieldName = _this.policies.getStoreFieldName(typename, selection, context.variables);
                    var processed = _this.processFieldValue(value, selection, context);
                    var merge = _this.policies.getFieldMergeFunction(typename, selection, context.variables);
                    if (merge || processed.mergeOverrides) {
                        mergeOverrides = mergeOverrides || Object.create(null);
                        mergeOverrides[storeFieldName] = context.merge(mergeOverrides[storeFieldName], { merge: merge, child: processed.mergeOverrides });
                    }
                    mergedFields = context.merge(mergedFields, (_a = {},
                        _a[storeFieldName] = processed.result,
                        _a));
                }
                else if (_this.policies.usingPossibleTypes &&
                    !(selection.directives &&
                        selection.directives.some(function (_a) {
                            var name = _a.name;
                            return name && (name.value === 'defer' || name.value === 'client');
                        }))) {
                    invariant.warn("Missing field " + resultFieldKey + " in " + JSON.stringify(result, null, 2).substring(0, 100));
                }
            }
            else {
                var fragment = getFragmentFromSelection(selection, context.fragmentMap);
                if (_this.policies.fragmentMatches(fragment, typename)) {
                    var processed = _this.processSelectionSet({
                        result: result,
                        selectionSet: fragment.selectionSet,
                        context: context,
                        mergeOverrides: mergeOverrides,
                        typename: typename,
                    });
                    mergedFields = context.merge(mergedFields, processed.result);
                    if (processed.mergeOverrides) {
                        mergeOverrides = context.merge(mergeOverrides, processed.mergeOverrides);
                    }
                }
            }
        });
        return {
            result: mergedFields,
            mergeOverrides: mergeOverrides,
        };
    };
    StoreWriter.prototype.processFieldValue = function (value, field, context) {
        var _this = this;
        if (!field.selectionSet || value === null) {
            return {
                result: process.env.NODE_ENV === 'production' ? value : cloneDeep(value),
            };
        }
        if (Array.isArray(value)) {
            var overrides_1;
            var result = value.map(function (item, i) {
                var _a = _this.processFieldValue(item, field, context), result = _a.result, mergeOverrides = _a.mergeOverrides;
                if (mergeOverrides) {
                    overrides_1 = overrides_1 || [];
                    overrides_1[i] = { child: mergeOverrides };
                }
                return result;
            });
            return { result: result, mergeOverrides: overrides_1 };
        }
        if (value) {
            var dataId = this.policies.identify(value, field.selectionSet, context.fragmentMap);
            if (typeof dataId === 'string') {
                this.writeSelectionSetToStore({
                    dataId: dataId,
                    result: value,
                    selectionSet: field.selectionSet,
                    context: context,
                });
                return { result: makeReference(dataId) };
            }
        }
        return this.processSelectionSet({
            result: value,
            selectionSet: field.selectionSet,
            context: context,
            typename: getTypenameFromResult(value, field.selectionSet, context.fragmentMap),
        });
    };
    return StoreWriter;
}());
export { StoreWriter };
function walkWithMergeOverrides(existingObject, incomingObject, overrides) {
    Object.keys(overrides).forEach(function (name) {
        var _a = overrides[name], merge = _a.merge, child = _a.child;
        var existingValue = existingObject && existingObject[name];
        var incomingValue = incomingObject && incomingObject[name];
        if (child) {
            walkWithMergeOverrides(existingValue, incomingValue, child);
        }
        if (merge) {
            if (process.env.NODE_ENV !== "production") {
                maybeDeepFreeze(existingValue);
            }
            incomingObject[name] = merge(existingValue, incomingValue);
        }
    });
}
//# sourceMappingURL=writeToStore.js.map